import { HomeReview, ReviewTemplate } from '../types';
import { OfflineResponse, OfflineReview } from './offlineDatabase';
import { buildTemplateForOfflineReview } from './offlineReviewHydration';
import {
  addOfflineOnDemandItemsToTemplate,
  collectOnDemandItemsFromTemplate,
  OfflineOnDemandItemRecord,
} from './offlineOnDemandItemMapper';
import {
  mapOfflineResponseToReviewResponse,
  mapOfflineReviewsToHomeReviews,
  mapOfflineReviewToHomeReview,
} from './reviewOfflineTransforms';

export interface OfflineReviewReadDatabase {
  getReviews(options?: {
    status?: string;
    syncStatus?: OfflineReview['syncStatus'];
    limit?: number;
    offset?: number;
  }): Promise<OfflineReview[]>;
  getReview(reviewId: number): Promise<OfflineReview | null>;
  getReviewByTempId(tempId: string): Promise<OfflineReview | null>;
  getResponsesByReviewId(reviewId: number): Promise<OfflineResponse[]>;
  getOfflineOnDemandItems?(reviewId: number): Promise<OfflineOnDemandItemRecord[]>;
  getPhotoMetadataByResponseId(responseId: number): Promise<any[]>;
}

export interface OfflineReviewReadDependencies {
  buildTemplateForOfflineReview?(
    offlineReview: OfflineReview,
    responses: OfflineResponse[]
  ): Promise<ReviewTemplate | null>;
  database: OfflineReviewReadDatabase;
  logger?: Pick<Console, 'warn'>;
}

export class OfflineReviewReadService {
  private readonly buildTemplate: (
    offlineReview: OfflineReview,
    responses: OfflineResponse[]
  ) => Promise<ReviewTemplate | null>;
  private readonly database: OfflineReviewReadDatabase;
  private readonly logger: Pick<Console, 'warn'>;

  constructor(dependencies: OfflineReviewReadDependencies) {
    this.buildTemplate =
      dependencies.buildTemplateForOfflineReview || buildTemplateForOfflineReview;
    this.database = dependencies.database;
    this.logger = dependencies.logger || console;
  }

  async getOfflineReview(reviewId: number | string): Promise<HomeReview> {
    try {
      const offlineReview = await this.getOfflineReviewRecord(reviewId);

      if (!offlineReview) {
        throw new Error(`Review ${reviewId} not found in offline storage`);
      }

      const responses = await this.database.getResponsesByReviewId(offlineReview.id);
      const template = await this.buildTemplate(offlineReview, responses);

      if (!template) {
        throw new Error(`Template ${offlineReview.templateId} is not available on this device yet. Connect to the internet and try again.`);
      }

      if (template.categories && this.database.getOfflineOnDemandItems) {
        try {
          const onDemandItems = await this.database.getOfflineOnDemandItems(offlineReview.id);
          addOfflineOnDemandItemsToTemplate(template, onDemandItems, offlineReview.id);
        } catch (error) {
          this.logger.warn('[OfflineReviewService] Failed to get on-demand items:', error);
        }
      }

      const homeReview = mapOfflineReviewToHomeReview(offlineReview, template);
      const allOnDemandItems = collectOnDemandItemsFromTemplate(template);
      if (allOnDemandItems.length > 0) {
        (homeReview as any).onDemandItems = allOnDemandItems;
      }

      for (const response of responses) {
        try {
          const photos = await this.database.getPhotoMetadataByResponseId(response.id);
          (response as any).photos = photos;
        } catch (photoError) {
          this.logger.warn(`[OfflineReviewService] Failed to load photos for response ${response.id}:`, photoError);
          (response as any).photos = [];
        }
      }

      homeReview.responses = responses.map(mapOfflineResponseToReviewResponse);

      return homeReview;
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Error getting offline review:', error);
      throw error;
    }
  }

  async getOfflineReviews(): Promise<HomeReview[]> {
    try {
      const offlineReviews = await this.database.getReviews({});
      return mapOfflineReviewsToHomeReviews(offlineReviews);
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Error getting offline reviews:', error);
      throw error;
    }
  }

  private getOfflineReviewRecord(reviewId: number | string): Promise<OfflineReview | null> {
    if (typeof reviewId === 'string' && reviewId.startsWith('temp_')) {
      return this.database.getReviewByTempId(reviewId);
    }

    return this.database.getReview(Number(reviewId));
  }
}
